import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { doDelete, get, patch, post } from '../../../helpers/httpClient';
import { toast } from 'react-toastify';

const SUPPORTED_TYPES = ['telegram'];

const CONFIG_FIELDS = {
  telegram: [
    { key: 'botToken', label: 'Bot token', placeholder: '123456789:AABBccdd…' },
    { key: 'chatId',   label: 'Chat ID',   placeholder: '-100123456789' },
  ],
};

function TransportForm({ websiteId, initial, onSaved, onCancel }) {
  const isEdit = !!initial;
  const [name,     setName]     = useState(initial?.name ?? '');
  const [type,     setType]     = useState(initial?.type ?? SUPPORTED_TYPES[0]);
  const [config,   setConfig]   = useState(() => ({ ...(initial?.config ?? {}) }));
  const [saving,   setSaving]   = useState(false);
  const [showHint, setShowHint] = useState(false);

  const setConfigField = (key, val) => setConfig(prev => ({ ...prev, [key]: val }));

  const handleTypeChange = (e) => {
    setType(e.target.value);
    setConfig({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Name is required.');
      return;
    }
    try {
      setSaving(true);
      const payload = isEdit
        ? { name: name.trim(), config }
        : { name: name.trim(), type, config };
      const result = isEdit
        ? await patch(`websites/${websiteId}/transports/${initial.id}`, payload)
        : await post(`websites/${websiteId}/transports`, payload);
      onSaved(result.transport);
    } catch {
      /* httpClient shows toast */
    } finally {
      setSaving(false);
    }
  };

  const fields = CONFIG_FIELDS[type] ?? [];
  const typeLabel = type.charAt(0).toUpperCase() + type.slice(1);

  return (
    <Form onSubmit={handleSubmit} className="mb-3">
      <Form.Group className="mb-3">
        <Form.Label>Name</Form.Label>
        <Form.Control
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="e.g. Telegram notifications"
        />
      </Form.Group>

      {!isEdit && (
        <Form.Group className="mb-3">
          <Form.Label>Type</Form.Label>
          <Form.Select value={type} onChange={handleTypeChange}>
            {SUPPORTED_TYPES.map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </Form.Select>
        </Form.Group>
      )}

      {fields.length > 0 && (
        <div className="mb-3">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <Form.Label style={{ margin: 0 }}>{typeLabel} configuration</Form.Label>
            {type === 'telegram' && (
              <button
                type="button"
                onClick={() => setShowHint(v => !v)}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--navy)', opacity: 0.5, fontSize: '0.78rem', fontWeight: 700 }}
              >
                {showHint ? '▲ how to set up' : '▼ how to set up'}
              </button>
            )}
          </div>
          {type === 'telegram' && showHint && (
            <div style={{ marginBottom: 10, padding: '10px 12px', borderLeft: '3px solid rgba(49,59,114,0.25)', background: 'rgba(49,59,114,0.04)', borderRadius: '0 6px 6px 0', fontSize: '0.82rem', lineHeight: 1.6 }}>
              <ol style={{ margin: 0, paddingLeft: 18 }}>
                <li style={{ marginBottom: 8 }}>
                  <strong>Create a bot</strong><br />
                  In Telegram, message <code>@BotFather</code>, send <code>/newbot</code>, choose a name — you'll receive a token like <code>123456789:ABCdefGhIJKlmno…</code>
                </li>
                <li style={{ marginBottom: 8 }}>
                  <strong>Add the bot to a group</strong><br />
                  Open the group → Members → Add → find the bot by username. For most cases a regular member is sufficient; some features require admin rights.
                </li>
                <li>
                  <strong>Get the group's chat_id</strong><br />
                  Post any message in the group, then open <code>https://api.telegram.org/bot&lt;TOKEN&gt;/getUpdates</code> in a browser — look for <code>"chat":{"{"}"id": -1001234567890{"}"}</code>.<br />
                  Alternatively, add <code>@RawDataBot</code> to the group — it will display the ID immediately.
                </li>
              </ol>
            </div>
          )}
          {fields.map(field => (
            <Form.Group key={field.key} className="mb-2">
              <Form.Label className="component-item-field-label">{field.label}</Form.Label>
              <Form.Control
                size="sm"
                value={config[field.key] ?? ''}
                onChange={e => setConfigField(field.key, e.target.value)}
                placeholder={field.placeholder}
              />
            </Form.Group>
          ))}
        </div>
      )}

      <div className="form-actions mt-3">
        <Button type="submit" className="primary-button" disabled={saving}>
          {saving ? <><Spinner animation="border" size="sm" className="me-2" />Saving…</> : 'Save'}
        </Button>
        <Button type="button" className="primary-button" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
      </div>
    </Form>
  );
}

function Transport({ website }) {
  const websiteId = website.id;

  const [transports,        setTransports]        = useState([]);
  const [loading,           setLoading]           = useState(true);
  const [creating,          setCreating]          = useState(false);
  const [editingTransport,  setEditingTransport]  = useState(null);
  const [deletingId,        setDeletingId]        = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await get(`websites/${websiteId}/transports`);
      setTransports(resp?.transports ?? []);
    } catch {
      /* httpClient shows toast */
    } finally {
      setLoading(false);
    }
  }, [websiteId]);

  useEffect(() => { load(); }, [load]);

  const handleCreated = (transport) => {
    setTransports(prev => [...prev, transport]);
    setCreating(false);
  };

  const handleUpdated = (transport) => {
    setTransports(prev => prev.map(t => (t.id === transport.id ? transport : t)));
    setEditingTransport(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this transport?')) return;
    try {
      setDeletingId(id);
      await doDelete(`websites/${websiteId}/transports/${id}`);
      setTransports(prev => prev.filter(t => t.id !== id));
    } catch {
      /* httpClient shows toast */
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return <div className="text-center my-4"><Spinner animation="border" /></div>;
  }

  if (editingTransport) {
    return (
      <div>
        <div className="structure-header-row mb-3">
          <h5 className="mb-0">Edit transport</h5>
        </div>
        <TransportForm
          websiteId={websiteId}
          initial={editingTransport}
          onSaved={handleUpdated}
          onCancel={() => setEditingTransport(null)}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="structure-header-row mb-3">
        <h5 className="mb-0">Transports</h5>
        {!creating && (
          <Button size="sm" type="button" className="primary-button m-0" onClick={() => setCreating(true)}>
            New transport
          </Button>
        )}
      </div>

      {creating && (
        <TransportForm
          websiteId={websiteId}
          onSaved={handleCreated}
          onCancel={() => setCreating(false)}
        />
      )}

      {!creating && transports.length === 0 && (
        <div className="gl-empty">No transports configured yet.</div>
      )}

      {!creating && transports.map(t => (
        <div key={t.id} className="gl-card">
          <div className="gl-card-info">
            <div className="gl-card-name">{t.name}</div>
            <div className="gl-card-meta">
              <span className="gl-meta-pill">{t.type}</span>
            </div>
          </div>
          <div className="gl-card-actions">
            <Button size="sm" type="button" className="primary-button m-0" onClick={() => setEditingTransport(t)}>
              Edit
            </Button>
            <button
              type="button"
              className="gl-delete-btn"
              onClick={() => handleDelete(t.id)}
              disabled={deletingId === t.id}
            >
              {deletingId === t.id ? <Spinner animation="border" size="sm" /> : 'Delete'}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

export default Transport;
